import { useEffect, useState } from 'react';
import { GenericAbortSignal } from 'axios';

import { useInfiniteQuery } from '@tanstack/react-query';

import type { Group as GroupType } from 'models/flexible/Group';
import Group, { GroupOptions } from 'models/flexible/Group';
import JobProcess, { JobProcessOptions } from 'models/flexible/JobProcess';
import { Paginated } from 'models/flexible/Paginated';
import type {
  AdminGroupCreateParams,
  AdminGroupIndexFields,
  AdminGroupIndexInclude,
  AdminGroupShowFields,
  AdminGroupShowInclude,
  AdminGroupUpdateParams,
} from 'services/api/fields/admin/group';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

export const adminGroupsQueryKeys = {
  index: ['adminGroups'] as const,
  show: (id: string) => ['adminGroups', id] as const,
};

const URL = 'admin/groups';

export type AdminGroupIndexParams = BaseIndexQueryParams & {
  clientId?: string;
  'clientId.not'?: string;
  networkClientId?: string;
  groupType?: string[];
  'id.not'?: string;
  type?: string;
  state?: string;
  isProvider?: boolean;
  isContracted?: string;
};

export async function adminIndexGroups<I extends AdminGroupIndexInclude = never>(
  params: AdminGroupIndexParams,
  signal?: GenericAbortSignal,
  includes?: I[]
): Promise<Paginated<GroupType<AdminGroupIndexFields<I>>>> {
  return http
    .get<
      Paginated<GroupOptions>
    >(URL, { params: { ...params, ...(includes?.length ? { include: includes.join(',') } : {}) }, signal })
    .then((res) => ({
      ...res.data,
      data: res.data.data.map((x) => new Group(x)),
    })) as Promise<Paginated<GroupType<AdminGroupIndexFields<I>>>>;
}

export async function adminCreateGroupExport(params: Record<string, any>) {
  return http.post<JobProcessOptions>(`${URL}/exports`, params).then((res) => new JobProcess(res.data));
}

export async function adminShowGroup<I extends AdminGroupShowInclude = never>(
  id: string,
  signal?: GenericAbortSignal,
  includes?: I[]
): Promise<GroupType<AdminGroupShowFields<I>>> {
  return http
    .get<GroupOptions>(`${URL}/${id}`, { params: { include: includes?.join(',') }, signal })
    .then((res) => new Group(res.data)) as Promise<GroupType<AdminGroupShowFields<I>>>;
}

export async function createGroup(params: AdminGroupCreateParams) {
  return http.post<GroupOptions>(URL, params).then((res) => new Group(res.data));
}

export async function updateGroup(id: string, params: AdminGroupUpdateParams) {
  return http.patch<GroupOptions>(`${URL}/${id}`, params).then((res) => new Group(res.data));
}

export async function archiveGroup(id: string) {
  return http.delete(`${URL}/${id}`);
}

export const useInfiniteAdminGroups = <I extends AdminGroupIndexInclude = never>(
  params: AdminGroupIndexParams,
  includes?: I[]
) => {
  const [search, setSearch] = useState<string>();

  const options = useInfiniteQuery({
    queryKey: [adminGroupsQueryKeys.index, { ...params, search, includes }],
    queryFn: ({ signal, pageParam }) =>
      adminIndexGroups({ pageSize: 10, ...params, search, page: pageParam }, signal, includes),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      return lastPage.meta.totalPages > lastPageParam ? lastPageParam + 1 : undefined;
    },
    enabled: false,
  });

  useEffect(() => {
    if (search !== undefined) options.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return { options, onDebouncedSearch: setSearch };
};
